import type { CustomerSessionVisibility } from "@shared-core/data/customer-api-types";
import { Eye } from "lucide-react";

// Makes scoped-ness legible. Without this, a person with own work only sees a
// short list and concludes the portal is broken (brief §11) — the absence has
// to read as a deliberate boundary, not as missing data.
//
// The wording follows the session's `visibility` and nothing else: "own_work"
// (orders they placed, quotes and proofs sent to them) or "customer_wide" ("See
// all of the customer's work"). There is no site scope in the one-customer model,
// so there is no third wording and no reconstructing one from a count of codes.
// Two of these can appear in the app at once, one per screen.

const ORDER_LABEL: Record<
  CustomerSessionVisibility,
  (customerName: string) => string
> = {
  own_work: () => "Showing orders placed by you",
  customer_wide: (customerName) => `Showing all of ${customerName}'s orders`,
};

// Spend is always the whole company's, whoever holds it and whatever they can
// see of orders, so it has one wording rather than one per visibility.
const spendLabel = (customerName: string) => `Spend for all of ${customerName}`;

export function PortalScopeNote({
  visibility,
  kind,
  customerName,
}: {
  visibility: CustomerSessionVisibility;
  kind: "orders" | "spend";
  customerName: string;
}) {
  const label =
    kind === "spend"
      ? spendLabel(customerName)
      : ORDER_LABEL[visibility](customerName);
  return (
    <p className="flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-xs text-muted-foreground">
      <Eye className="size-3.5 shrink-0" />
      {label}
    </p>
  );
}
